import { isPlatformServer } from '@angular/common';
import { Inject, inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CONNECT_ISSUER } from '~/auth/providers/connect.provider';
import { AuthService } from '~/auth/services/auth/auth.service';
import { UserService } from '~/auth/services/user/user.service';

@Injectable({
  providedIn: 'root',
})
class PermissionsService {
  constructor(
    private readonly userService: UserService,
    private readonly authService: AuthService,
    private readonly router: Router,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    // if user is unlogged, fallback guard login to connect (checkout use case)
    @Inject(CONNECT_ISSUER) private readonly connectIssuer: string,
    private readonly translateService: TranslateService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | UrlTree {
    if (isPlatformServer(this.platformId)) {
      return this.router.parseUrl('/auth/waiting');
    }

    return this.userService.isAuthenticated().pipe(
      map((isLoggedIn) => {
        if (!isLoggedIn) {
          // current issuer or fallback to conncet
          const issuer =
            this.authService.getCurrentAuthProvider() || this.connectIssuer;
          // not logged, login with connect with redirect to current url
          this.authService.login(issuer, {
            from: state.url,
            lang: this.translateService.currentLang,
          });
        }

        return isLoggedIn;
      })
    );
  }
}

export const AuthGuard: CanActivateFn = (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> | Promise<boolean> | UrlTree => {
  return inject(PermissionsService).canActivate(next, state);
};
